import { FC, useEffect, useMemo, useReducer, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Table, Button, Icon, TooltipInfo } from '../';
import IndeterminateCheckbox from './components/IndeterminateCheckbox';
import HousingAssociationReportCell from './components/HousingAssociationReportCell';
import AdPdfCell from './components/AdPdfCell';
import { useProjectContext } from '@/context/ProjectContext';
import { useTableCalculations } from '@/context/hooks/useTableCalculations';
import {
  ButtonSize,
  ColumnEnum,
  HomeTypeEnum,
  Icons,
  OwnershipType,
  TableSize,
  TabsType,
  IconSize,
  ButtonColor
} from '@/enums';
import strings, { homeTypes, ownershipTypes } from '@/language/Swedish';
import { SalesRefTableType } from '@/types/dataTypes';
import { formatNumber, formatPrice, removeStreetNumberFromString } from '@/helpers';
import { SortingType, handleColumnClick, animateRowRemoval } from './helpers';
import { SalesSummaryResponseType } from '@/types/apiTypes';
import { exportSoldToExcel } from '@/helpers/exportToExcel';
import useUser from '@/api/queries/useUser';
import { ROLE_TYPES } from '@/constants';
const HideButton = styled(Button)`
  ${tw`flex justify-start p-0 w-full h-full`};
`;

const StyledIcon = styled(Icon)`
  transform: rotate(45deg);
`;

const DownloadButton = styled(Button)`
  border: 1px solid;
  ${tw`absolute flex items-center justify-center right-2.5 border-gray-300`};
  top: 9px;
`;

interface Props {
  tableData: SalesRefTableType[];
  tableSummary: SalesSummaryResponseType | null;
  size?: TableSize;
  hideClientData?: boolean;
  updateExcluded?: (type: TabsType, id: string) => void;
  className?: string;
  report?: boolean;
}

const SoldTable: FC<Props> = ({
  tableData,
  size,
  hideClientData,
  tableSummary,
  updateExcluded,
  className,
  report
}) => {
  const { soldCalculations, selectedSoldTableData } = useTableCalculations();
  const [sorting, setSorting] = useState<SortingType[]>([]);
  const [rowSelection, setRowSelection] = useState<Set<string>>(new Set());
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = localStorage.getItem('sold-column-order');
    // Drop any previously saved hideRow so it always stays rightmost.
    return stored
      ? JSON.parse(stored).filter((id: string) => id !== 'hideRow')
      : [];
  });

  useEffect(() => {
    localStorage.setItem('sold-column-order', JSON.stringify(columnOrder));
  }, [columnOrder]);
  const {
    project: { id, soldTableIds, soldTableColumns, soldTableReportColumns, sale_references_sort },
    setProject,
    toggleSelectedTableIds,
  } = useProjectContext();

  const rerender = useReducer(() => ({}), {})[1];
  const { data: userData } = useUser();
  const hasExcelButtonRole = userData?.roles.includes(ROLE_TYPES.SALES_REF_LIMIT_250);

  useEffect(() => {
    if (soldTableIds?.selected) {
      const initialSelection = new Set<string>();
      soldTableIds.selected.forEach((id) => {
        initialSelection.add(id);
      });
      setRowSelection(initialSelection);
    }
  }, [soldTableIds.selected]);

  useEffect(() => {
    rerender();
  }, [rowSelection, sorting, tableData]);

  useEffect(() => {
    if (sorting[0] != sale_references_sort) {
      setProject((prevState) => ({
        ...prevState,
        sale_references_sort: sorting[0]
      }));
    }
  }, [sorting]);

  useEffect(() => {
    if (sale_references_sort) {
      setSorting([sale_references_sort]);
    } else {
      setSorting([]);
    }
  }, [id]);

  const toRowSelectionState = (selectedSet: Set<string>): RowSelectionState => {
    const rowSelectionState: RowSelectionState = {};
    selectedSet.forEach((id) => {
      rowSelectionState[id] = true;
    });
    return rowSelectionState;
  };

  const columns = useMemo<ColumnDef<SalesRefTableType>[]>(() => {
    const allColumns: ColumnDef<SalesRefTableType>[] = [
      {
        id: 'index',
        header: ({ table }) => (
          <span
            onClick={() => {
              if (sorting.length !== 0) {
                table.resetSorting();
                localStorage.removeItem('table-sorting');
              }
            }}>
            {sorting.length !== 0 ? (
              <TooltipInfo content={strings.table.resetSorting}>
                <div>
                  <Icon size={IconSize.medium} icon={Icons.refresh} />
                </div>
              </TooltipInfo>
            ) : (
              ''
            )}
          </span>
        ),
        cell: ({ row }) => {
          if (size === TableSize.small || size === TableSize.medium) {
            return <div style={{width: "20px"}}>{`${row.id} `}</div>;
          }
          return <div style={{width: "20px"}}>{row.index + 1}</div>;
        },
        meta: { pin: 'left' }
      },
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => {
          const allRowsSelected = table.getRowModel().rows.every((row) => {
            return soldTableIds.selected?.has(row.id);
          });

          return (
            <IndeterminateCheckbox
              {...{
                checked: allRowsSelected,
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: () => {
                  const isAllSelected = table
                    .getRowModel()
                    .rows.every((row) => {
                      return soldTableIds.selected?.has(row.id);
                    });

                  const newSelected = new Set(rowSelection);

                  if (isAllSelected) {
                    table.getRowModel().rows.forEach((row) => {
                      toggleSelectedTableIds(TabsType.sold, row.id);
                    });
                  } else {
                    table.getRowModel().rows.forEach((row) => {
                      if (!soldTableIds?.selected?.has(row.id)) {
                        toggleSelectedTableIds(TabsType.sold, row.id);
                      }
                    });
                  }

                  setRowSelection(newSelected);
                },
              }}
            />
          );
        },
        cell: ({ row }) => (
          <div className="flex w-5 items-center justify-between">
            <IndeterminateCheckbox
              {...{
                checked: soldTableIds?.selected?.has(row.id),
                onChange: () => toggleSelectedTableIds(TabsType.sold, row.id),
              }}
            />
          </div>
        ),
      },
      {
        id: ColumnEnum.street,
        accessorFn: (row) => row.street,
        cell: (info) => {
          if (hideClientData)
            return removeStreetNumberFromString(info.getValue() as string);
          else return info.getValue();
        },
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.street}
          </span>
        ),
        footer: () => (
          selectedSoldTableData?.length === 0 ? (<span>
            {strings.table.footers.distance}
            {tableSummary?.average_distance}m
          </span> ) : ""
        ),
      },
      {
        accessorFn: (row) => row.contractDate,
        id: ColumnEnum.contractDate,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.contractDate}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.contractDate}
            </span>
          </TooltipInfo>
        ),
        footer: () => <span>{soldCalculations?.average_contract_date ? soldCalculations.average_contract_date : tableSummary?.average_contract_date ? tableSummary?.average_contract_date : ''}</span>,
      },
      {
        accessorFn: (row) => row.soldPrice,
        id: ColumnEnum.soldPrice,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.sold_price}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_price ?
              formatPrice(soldCalculations.average_price) : tableSummary?.average_price ? formatPrice(tableSummary?.average_price) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.livingArea,
        id: ColumnEnum.livingArea,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.livingArea}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_living_area ? soldCalculations.average_living_area : tableSummary?.average_living_area ? tableSummary?.average_living_area : ''}</span>,
      },
      {
        accessorFn: (row) => row.rooms,
        id: ColumnEnum.rooms,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.rooms}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_rooms ? soldCalculations.average_rooms : tableSummary?.average_rooms ? tableSummary?.average_rooms : ''}</span>,
      },
      {
        accessorFn: (row) => row.floorFloors,
        id: ColumnEnum.floorFloors,
        maxSize: 50,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.floorFloors}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.floorFloors}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.constructionYear,
        id: ColumnEnum.constructionYear,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.constructionYear}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_construction_year ? soldCalculations.average_construction_year : tableSummary?.average_construction_year ? tableSummary?.average_construction_year : ''}</span>,
      },
      {
        accessorFn: (row) => row.plotArea,
        id: ColumnEnum.plotArea,
        cell: (info) => formatNumber(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.plotArea}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_plot_area ? formatNumber(soldCalculations.average_plot_area as unknown as number) : tableSummary?.average_plot_area ? formatNumber(tableSummary?.average_plot_area as unknown as number) : ''}</span>,
      },
      {
        accessorFn: (row) => row.ancillaryArea,
        id: ColumnEnum.ancillaryArea,
        cell: (info) => formatNumber(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ancillaryArea}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_ancillary_area ? formatNumber(soldCalculations.average_ancillary_area) : tableSummary?.average_ancillary_area ? formatNumber(tableSummary?.average_ancillary_area) : ''}</span>,
      },
      {
        accessorFn: (row) => row.soldPriceSqm,
        id: ColumnEnum.soldPriceSqm,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.soldPriceSqm}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_price_sqm ? formatPrice(soldCalculations.average_price_sqm) : tableSummary?.average_price_sqm ? formatPrice(tableSummary?.average_price_sqm) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.recountedPrice,
        id: ColumnEnum.recountedPrice,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.recountedPrice}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.recountedPrice}
            </span>
          </TooltipInfo>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_price_today ?
              formatPrice(soldCalculations.average_price_today) : tableSummary?.average_price_today ? formatPrice(tableSummary?.average_price_today) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.recountedPriceSqm,
        id: ColumnEnum.recountedPriceSqm,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.recountedPriceSqm}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_price_today_sqm ? formatPrice(soldCalculations.average_price_today_sqm) : tableSummary?.average_price_today_sqm ? formatPrice(tableSummary?.average_price_today_sqm) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.taxationValue,
        id: ColumnEnum.taxasionValue,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.taxationValue}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_taxation_value ? formatPrice(soldCalculations.average_taxation_value) : tableSummary?.average_taxation_value ? formatPrice(tableSummary?.average_taxation_value) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.taxationYear,
        id: ColumnEnum.taxationYear,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.taxationYear}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_taxation_year}</span>,
      },
      {
        accessorFn: (row) => row.priceTaxationRatio,
        id: ColumnEnum.priceTaxationRatio,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.priceTaxationRatio}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_price_taxation_ratio ? soldCalculations.average_price_taxation_ratio : tableSummary?.average_price_taxation_ratio ? String(tableSummary?.average_price_taxation_ratio).replace('.', ',') : ''}</span>,
      },

      {
        accessorFn: (row) => row.monthlyFee,
        id: ColumnEnum.monthlyFee,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.monthlyFee}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_monthly_fee ? formatPrice(soldCalculations.average_monthly_fee) : tableSummary?.average_monthly_fee ? formatPrice(tableSummary?.average_monthly_fee) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.ownSale,
        id: ColumnEnum.ownSale,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ownSale}
          </span>
        ),
      },

      {
        accessorFn: (row) => row.monthlyFeeSqm,
        id: ColumnEnum.monthlyFeeSqm,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.monthlyFeeSqm}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.monthlyFeeSqm}
            </span>
          </TooltipInfo>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_monthly_fee_sqm ? formatPrice(soldCalculations.average_monthly_fee_sqm) : tableSummary?.average_monthly_fee_sqm ? formatPrice(tableSummary?.average_monthly_fee_sqm) : ''}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.source,
        id: ColumnEnum.source,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.source}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.source}
            </span>
          </TooltipInfo>
        ),
      },

      //adsTable
      {
        accessorFn: (row) => row.adRegisteredDate,
        id: ColumnEnum.adRegisteredDate,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.adRegisteredDate}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_ad_registered_date}</span>,
      },
      {
        id: ColumnEnum.adDeleteDate,
        accessorFn: (row) => row.ad_delete_date,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.adDeleteDate}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_ad_delete_date}</span>,
      },

      {
        accessorFn: (row) => row.adAdvertisingDays,
        id: ColumnEnum.adAdvertisingDays,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.advertisingDays}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.adAvertisingDays}
            </span>
          </TooltipInfo>
        ),
        footer: () => <span>{soldCalculations?.average_ad_advertising_days}</span>,
      },
      {
        accessorFn: (row) => row.adPrice,
        id: ColumnEnum.adPrice,
        cell: (info) =>
          info.getValue() ? (
            formatPrice(info.getValue() as number)
          ) : (
            <span></span>
          ),

        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.adPrice}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_ad_price &&
              formatPrice(soldCalculations.average_ad_price)}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.adPriceSqm,
        id: ColumnEnum.adPriceSqm,
        cell: (info) =>
          info.getValue() ? (
            formatPrice(info.getValue() as number)
          ) : (
            <span></span>
          ),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.adPriceSqm}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_ad_price_sqm &&
              formatPrice(soldCalculations.average_ad_price_sqm)}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.firm,
        id: ColumnEnum.firm,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.firm}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.prospectUrl,
        id: ColumnEnum.prospectUrl,
        cell: (info) => <AdPdfCell prospectUrl={info.getValue() as string} />,
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.prospectUrl}
          </span>
        ),
      },

      //RealEstateTable
      {
        accessorFn: (row) => row.propertyUnitDesignation,
        id: ColumnEnum.propertyUnitDesignation,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.propertyUnitDesignation}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.valuationYear,
        id: ColumnEnum.valuationYear,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.valuationYear}
          </span>
        ),
        footer: () => <span>{soldCalculations?.average_construction_value_year}</span>,
      },
      {
        accessorFn: (row) => row.homeType,
        id: ColumnEnum.homeType,
        cell: (info) => homeTypes[info.getValue() as HomeTypeEnum],
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.homeType}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.ownershipType,
        id: ColumnEnum.ownershipType,
        cell: (info) => ownershipTypes[info.getValue() as OwnershipType],
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ownershipType}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.housingAssociationReportYear,
        id: ColumnEnum.housingAssociationReportYear,
        cell: (info) => (
          <HousingAssociationReportCell
            ownerId={info.row.original.ownerId}
            housingAssociationReportYear={
              info.getValue() as number | null
            }
          />
        ),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.housingAssociationReportYear}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.ownerName,
        id: ColumnEnum.ownerName,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ownerName}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.ownerId,
        id: ColumnEnum.ownerId,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ownerId}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.allabrfUrl,
        id: ColumnEnum.allabrfUrl,
        cell: (info) =>
          info.getValue() ? (
            <a
              href={info.getValue() as string}
              target="_blank"
              className="flex gap-2">
              {strings.table.link}
              <StyledIcon icon={Icons.arrowUp} />
            </a>
          ) : (
            <span></span>
          ),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.allabrfUrl}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.allabolagUrl,
        id: ColumnEnum.allabolagUrl,
        cell: (info) =>
          info.getValue() ? (
            <a
              href={info.getValue() as string}
              target="_blank"
              className="flex gap-2">
              {strings.table.link}
              <StyledIcon icon={Icons.arrowUp} />
            </a>
          ) : (
            <span></span>
          ),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.allabolagUrl}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.shoreDistanceType,
        id: ColumnEnum.shoreDistanceType,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            <TooltipInfo content={strings.table.tooltip.shoreDistanceType}>
              <span
                onClick={() =>
                  handleColumnClick(setSorting, column.columnDef.id)
                }>
                {strings.table.columns.shoreDistanceType}
              </span>
            </TooltipInfo>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.waterSewerageType,
        id: ColumnEnum.waterSewerageType,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.waterSewerageType}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.waterSewerageType}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.alterationYear,
        id: ColumnEnum.alterationYear,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.alterationYear}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.alterationYear}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.standardPoints,
        id: ColumnEnum.standardPoints,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.standardPoints}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.standardPoints}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.leaseholdFee,
        id: ColumnEnum.leaseholdFee,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.leaseholdFee}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_leasehold_fee &&
              formatPrice(soldCalculations.average_leasehold_fee)}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.leaseholdPeriod,
        id: ColumnEnum.leaseholdPeriod,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.leaseholdPeriod}
          </span>
        ),
        footer: () => (
          <span>
            {soldCalculations?.average_leasehold_period &&
              formatPrice(soldCalculations.average_leasehold_period)}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.subdivisionType,
        id: ColumnEnum.subdivisionType,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.subdivisionType}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.subdivisionType}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.balcony,
        id: ColumnEnum.balcony,
        cell: (info) => {
          const value = info.getValue();
          return value === 1 || value === '1' ? 'Ja' : '';
        },
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.balcony}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.elevator,
        id: ColumnEnum.elevator,
        cell: (info) => {
          const value = info.getValue();
          return value === 1 || value === '1' ? 'Ja' : '';
        },
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.elevator}
          </span>
        ),
      },

      {
        id: 'hideRow',
        enableSorting: false,
        header: () => (
          <TooltipInfo content={strings.table.tooltip.hide}>
            <span>{strings.table.hide}</span>
          </TooltipInfo>
        ),
        cell: ({ row }) => (
          <HideButton
            icon={Icons.eye}
            size={ButtonSize.large}
            onClick={(e) => {
              if (updateExcluded)
                animateRowRemoval(e.currentTarget, () =>
                  updateExcluded(TabsType.sold, row.original.id)
                );
            }}
          />
        ),
      },
    ];

    const columns = allColumns.filter((column) => {
      if (
        column.id === 'select' ||
        column.id === 'hideRow' ||
        column.id === 'index'
      )
        return true;
      return (report ? soldTableReportColumns : soldTableColumns).find((item) => item.id === column.id)?.isVisible;
    });

    if (size === TableSize.small || size === TableSize.medium) {
      return columns.filter(
        (column) =>
          column.id !== 'select' &&
          column.id !== 'hideRow' &&
          column.id !== ColumnEnum.prospectUrl &&
          column.id !== ColumnEnum.allabrfUrl &&
          column.id !== ColumnEnum.allabolagUrl &&
          column.id !== ColumnEnum.housingAssociationReportYear
      );
    }

    return columns;
  }, [rowSelection, soldTableColumns, soldTableReportColumns, sorting, size, soldCalculations]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (
    updaterOrValue
  ) => {
    setRowSelection((prev) => {
      const newSelected = new Set<string>();
      if (typeof updaterOrValue === 'function') {
        const updatedState = updaterOrValue(toRowSelectionState(prev));
        Object.keys(updatedState).forEach((id) => {
          if (updatedState[id]) newSelected.add(id);
        });
      } else {
        Object.keys(updaterOrValue).forEach((id) => {
          if (updaterOrValue[id]) newSelected.add(id);
        });
      }

      Array.from(newSelected).forEach((id) =>
        toggleSelectedTableIds(TabsType.sold, id)
      );

      return newSelected;
    });
  };

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection: toRowSelectionState(rowSelection),
      sorting,
      columnOrder,
      pagination: {
        pageIndex: 0,
        pageSize: 250,
      },
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const hideFooter = tableSummary ? true : false;
  return (
    <Table
      hideFooter={hideFooter}
      pinSelectColumn={!report}
      table={table}
      rowSelection={toRowSelectionState(rowSelection)}
      size={size}
      sorting={sale_references_sort}
      className={className}
    >
      <DownloadButton
        toolTip={!hasExcelButtonRole ? strings.table.noExcelExport : undefined}
        color={ButtonColor.primary}
        size={ButtonSize.small}
        disabled={!hasExcelButtonRole}
        onClick={() =>
          exportSoldToExcel(tableData, strings.dashboard.excelName)
        }>
        <Icon icon={Icons.excel} size={IconSize.medium} />
      </DownloadButton>
    </Table>
  );
};

export default SoldTable;
